/**
 * Zoom in / zoom out / fit / reset cluster and the minimap for a case's Cytoscape views
 * (Evidence and Consolidated), so both behave alike. The visuals are the Workbench's own
 * CanvasZoomControls and GraphMinimap, imported not copied; this wrapper only supplies the
 * behaviour from cyViewport.ts and keeps the zoom percentage current.
 */
import { useEffect, useState } from 'react';
import type cytoscape from 'cytoscape';
import { CanvasZoomControls } from '../../components/workbench/CanvasZoomControls';
import { GraphMinimap } from '../../components/workbench/GraphMinimap';
import { ZOOM_STEP, fitView, resetView, zoomBy, type ElesGetter } from './cyViewport';

interface Props {
  /** The live instance; null until the view has mounted its graph. */
  cy: cytoscape.Core | null;
  getEles?: ElesGetter;
}

const alive = (cy: cytoscape.Core | null): cy is cytoscape.Core => !!cy && !cy.destroyed();

export function CyZoomControls({ cy, getEles }: Props) {
  const [zoomPct, setZoomPct] = useState(100);
  const [nodeCount, setNodeCount] = useState(0);

  useEffect(() => {
    if (!alive(cy)) return;
    const sync = () => { if (alive(cy)) setZoomPct(Math.round(cy.zoom() * 100)); };
    const count = () => { if (alive(cy)) setNodeCount(cy.nodes().length); };
    sync(); count();
    cy.on('zoom', sync);
    cy.on('add remove', count);
    return () => {
      if (!cy.destroyed()) { cy.off('zoom', sync); cy.off('add remove', count); }
    };
  }, [cy]);

  return (
    <>
      <CanvasZoomControls
        zoomLevel={zoomPct}
        onZoomIn={() => zoomBy(cy, ZOOM_STEP)}
        onZoomOut={() => zoomBy(cy, 1 / ZOOM_STEP)}
        onFitToView={() => fitView(cy, getEles)}
        onResetZoom={() => resetView(cy, getEles)}
      />
      {alive(cy) && <GraphMinimap cy={cy} nodeCount={nodeCount} threshold={30} />}
    </>
  );
}
